"use client"
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { SocialProfile } from '@/lib/social/types'
import { errorMessage, socialApi, useSocial } from './client'

type FollowState = { relationship: SocialProfile['relationship']; busy: boolean }
function createFollowStore() {
  let snapshot = new Map<string, FollowState>()
  const initial = snapshot, listeners = new Set<() => void>()
  return {
    subscribe(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener) } },
    getSnapshot: () => snapshot, getServerSnapshot: () => initial,
    set(id: string, value: FollowState) { snapshot = new Map(snapshot).set(id, value); listeners.forEach(listener => listener()) },
    reconcile(id: string, relationship: SocialProfile['relationship']) { const current = snapshot.get(id); if (current && !current.busy && current.relationship !== relationship) { snapshot = new Map(snapshot); snapshot.delete(id); listeners.forEach(listener => listener()) } },
  }
}
const followStores = new WeakMap<object, ReturnType<typeof createFollowStore>>()

export function FollowButton({ profile, onChange, className }: { profile: SocialProfile; onChange?: (relationship?: SocialProfile['relationship']) => void; className?: string }) {
  const { requireProfile, resourceCache } = useSocial()
  const [fallbackScope] = useState(() => ({}))
  const store = useMemo(() => { const key = resourceCache || fallbackScope; let current = followStores.get(key); if (!current) { current = createFollowStore(); followStores.set(key, current) } return current }, [resourceCache, fallbackScope])
  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getServerSnapshot), current = snapshot.get(profile.id)
  const state = current?.relationship || profile.relationship, busy = current?.busy || false
  useEffect(() => { store.reconcile(profile.id, profile.relationship) }, [store, profile.id, profile.relationship])
  if (profile.relationship === 'self' || profile.blocked) return null
  async function toggle() {
    if (!requireProfile() || store.getSnapshot().get(profile.id)?.busy) return
    const previous = state, following = state === 'none'
    store.set(profile.id, { relationship: following ? profile.private ? 'pending' : 'accepted' : 'none', busy: true })
    try {
      const result = await socialApi<{ relationship: SocialProfile['relationship'] }>(`/follows/${profile.id}`, { method: 'PUT', body: { following } })
      store.set(profile.id, { relationship: result.relationship, busy: false }); onChange?.(result.relationship)
    } catch (error) { store.set(profile.id, { relationship: previous, busy: false }); toast.error(errorMessage(error)) }
  }
  return <Button size="sm" className={cn("rounded-full px-4 text-xs font-semibold", className)} variant={state === 'none' ? 'default' : 'outline'} disabled={busy} aria-busy={busy} onClick={() => void toggle()}>{state === 'accepted' ? 'Unfollow' : state === 'pending' ? 'Requested' : 'Follow'}</Button>
}
